import type { ComponentType } from 'react'
import React, { useCallback, forwardRef, useState, useEffect } from 'react'
import { withDependencies } from '@wix/thunderbolt-ioc'
import { useService } from '@wix/services-manager-react'
import { ContextProviderRegistrySymbol } from './symbols'
import type { ContextProviderRegistry } from './types'
import { ContextProviderServiceDefinition } from './services'
import { ExperimentsSymbol } from '@wix/thunderbolt-symbols'
import type { Experiments } from '@wix/thunderbolt-symbols'

export const ContextProviderWrapper = withDependencies(
	[ContextProviderRegistrySymbol, ExperimentsSymbol],
	(contextProviderRegistry: ContextProviderRegistry, experiments: Experiments) => {
		const servicesInfraExperiment = Boolean(experiments['specs.thunderbolt.servicesInfra'])
		const contextProvidersEnabled = Boolean(experiments['specs.thunderbolt.contextProviders'])

		const shouldWrap = servicesInfraExperiment && contextProvidersEnabled

		if (!shouldWrap) {
			return {
				wrapComponent(Component: ComponentType<any>) {
					return Component
				},
			}
		}

		return {
			wrapComponent: (Component: ComponentType<any>) => {
				return forwardRef<any, any>((props, ref) => {
					const contextProviderService = useService(ContextProviderServiceDefinition)
					const [, setTick] = useState(0)
					const forceUpdate = useCallback(() => setTick((tick) => tick + 1), [])

					// subscribe to context changes on mount and unsub on unmount
					useEffect(() => {
						return contextProviderService.subscribeToContextChanges(props.compId, forceUpdate)
					}, [props.compId, forceUpdate, contextProviderService])

					const attachedContexts = contextProviderService.getAttachedContexts(props.id)

					const OriginalComponent = <Component {...props} ref={ref} />

					// wrap the component from the inside out
					let ComponentToWrap = OriginalComponent
					for (let i = attachedContexts.length - 1; i >= 0; i--) {
						const { contextType, data } = attachedContexts[i]
						const module = contextProviderRegistry.getContextProvider(contextType)
						const ContextProvider = module?.default
						if (!ContextProvider) {
							// Avoid wrapping the component if at least one context provider is not loaded
							return OriginalComponent
						}

						const child = ComponentToWrap
						ComponentToWrap = <ContextProvider {...(data || {})}>{child}</ContextProvider>
					}

					return ComponentToWrap
				})
			},
		}
	}
)
